import { useEffect, useMemo, useState } from 'react';
import { sharedGet } from './sharedGet';
import { useCategories } from './catalogCategories';
import { useItemSchematics } from './catalogItems';
import { useListing } from './listingStore';
import { groupOfCategory } from './partsRun';
import type { PictureRef } from './pictureRef';
import type { ProductShot } from './threeDApi';
import { directionRank, OTHER_DIRECTION } from './viewDirections';

// Which pictures may go in front of the model with a repair question, and how many of
// each. One module because it is one question — "what is there to show it?" — asked of
// two different tables, and the modal that draws the answer should not have to know that.
//
// TWO KINDS, and they are not the same kind of evidence:
//
//   the DRAWINGS   what the part SHOULD be. The manual's line art, cropped to this part.
//                  Every repair is judged against one, and without any the proposal rests
//                  on the measurements and the sentence alone.
//   the PRODUCT    what it is a piece OF. The notice's listing photographs of the
//                  assembled furniture: which way up the part goes, which end meets the
//                  seat. Context, not evidence — nothing is measured off it.
//
// THE PRODUCT PHOTOS ARE FOR COMPONENTS ONLY. A chair leg is a piece of one particular
// chair and is shaped by where it sits in it; a barrel nut is the same barrel nut in every
// piece of furniture ever built, and a photograph of the wardrobe it went into says
// nothing about the cross on its end. Offering them there would be offering tokens.

export type AskGroup = {
  kind: 'schematic' | 'listing';
  refs: PictureRef[];
  // How many of this kind may be sent at once. The twins of MAX_DRAWINGS and MAX_CONTEXT
  // in mesh_advice.py — the server enforces both by dropping the extras, and a picture
  // somebody selected that was never sent is worse than a limit.
  max: number;
};

export const MAX_DRAWINGS = 4;
export const MAX_CONTEXT = 2;

// One notice's stored product photographs, or none. Read straight from the endpoint the
// Phase 1 gallery reads: these are the pictures the manual's own listing was found with,
// and they are already on this page somewhere else.
//
// The URL comes back with the id because that is the only join there is to the DIRECTION
// each picture was classified as: the classification is a Phase 1 answer held in the
// document's own state, keyed by the gallery URL, and the table stores the bytes and
// nothing about what they are of. See useProductShots.
function useListingImages(docId: string | null): { id: number; url: string }[] {
  const [ids, setIds] = useState<{ id: number; url: string }[]>([]);
  useEffect(() => {
    if (!docId) {
      setIds([]);
      return;
    }
    let alive = true;
    sharedGet<{ images: { id: number; url: string }[] }>(`/api/notice/listing-images/${encodeURIComponent(docId)}`)
      .then(({ data }) => alive && setIds((data.images || [])
        .map((i) => ({ id: i.id, url: i.url || '' }))))
      // Silent. Nobody asked for these; the drawings are the half that matters, and a
      // dialog that reported a failed fetch of the optional half would be reporting it in
      // front of the question it is there to help ask.
      .catch(() => alive && setIds([]));
    return () => {
      alive = false;
    };
  }, [docId]);
  return ids;
}

export function useAskPictures(
  docId: string | null,
  itemId: number | null,
  category: string | null,
): AskGroup[] {
  const schematics = useItemSchematics(itemId);
  // SUBSCRIBED to the ontology rather than reading its cache, because the answer decides
  // whether a whole group of pictures exists. Unloaded, every category reads as a
  // component — and that is the branch that offers the product photographs, so a barrel
  // nut would be shown them for as long as the fetch took.
  const isComponent = groupOfCategory(category, useCategories()) === 'Components';
  const listing = useListingImages(isComponent ? docId : null);
  return useMemo(() => {
    const groups: AskGroup[] = [{
      kind: 'schematic',
      refs: schematics.map((s) => ({ source: 'schematic' as const, imageId: s.id })),
      max: MAX_DRAWINGS,
    }];
    if (isComponent && listing.length) {
      groups.push({
        kind: 'listing',
        refs: listing.map(({ id }) => ({ source: 'listing' as const, imageId: id })),
        max: MAX_CONTEXT,
      });
    }
    return groups;
  }, [schematics, listing, isComponent]);
}

// How many photographs of the assembled product go with an ORIENTATION question. Eight,
// where a repair question takes two: there the photograph is context for a part being judged
// against its drawing, and here it is the whole of the evidence. The twin of MAX_PHOTOS in
// mesh_orient.py.
//
// IT WAS FOUR, back when this was the classified square-on views and nothing else — front,
// back, one profile, and there is not a fourth worth having. The set is now every classified
// picture this part was actually FOUND in (see below), which on a chair can be six or seven
// of the strip. Eight is that strip once the near-duplicates are gone, which is the number
// listingCropsRun already reads for the same reason.
//
// IT IS A BACKSTOP AND NOT THE CONTROL. Every picture in the prompt carries a toggle now, so
// what goes is decided by looking rather than by a cap deciding it silently — see orientShots.
export const MAX_PRODUCT_SHOTS = 8;

// Nothing found, as a constant: a new empty Set on every render would re-run the memo below
// on every render, and this hook's whole output is an array other memos are keyed by.
const NOTHING: ReadonlySet<string> = new Set();

// THE PICTURES AN ORIENTATION CALL IS ASKED WITH: the notice's own photographs of the
// assembled product, each carrying the side it was classified as showing.
//
// TWO THINGS A PICTURE HAS TO HAVE, and it needs both.
//
//   THE PART IS ON IT. `showing` is the URLs this part was found in — Phase 1's crops, which
//   the page holds and this module does not. The call matches six renders of a mesh against
//   these photographs, so a picture the part cannot be found in gives the renders nothing to
//   be held beside: it is a chair, the mesh is a leg, and the only honest thing to do with
//   the pair is guess.
//
//   AND ITS VIEWPOINT HAS A NAME. A photograph classified 'other' — an in-situ shot, two
//   chairs at a table, a detail — is one the prompt can say nothing about, so the model is
//   told "here is the product, from an angle nobody has classified" and has to work out both
//   what it is looking at and which side of the part faces it. Every other picture in the
//   call answers the first half for it, and the odd one out is where a confident wrong
//   answer comes from.
//
// BOTH RULES WERE ONCE ONE OR THE OTHER, and each alone was wrong in its own direction. The
// classified views alone asked the question over pictures the part is not in — a chair's rear
// leg frame is in the three-quarter shots and the close-up and in NEITHER square-on view.
// Adding anything with a crop on it let the in-situ shots back in with no viewpoint to their
// name. What is left is the intersection, which is what the call was always about: the
// product seen from a named side, with this part outlined on it.
//
// ORDERED BY THE VOCABULARY'S OWN RANK — front, back, left, right, top, bottom, then the two
// 45°s — so the prompt's numbering opens with the pictures that settle the most. Stable, so
// pictures of one direction keep the merchant's order.
//
// COMPONENTS ONLY, the same rule the repair dialog keeps and for a stronger reason: a barrel
// nut has no front, and no photograph of the wardrobe it went into will give it one.
export function useProductShots(
  docId: string | null, category: string | null,
  // The photographs THIS part has a crop in, by gallery URL. Empty means empty: a part
  // Phase 1 has not found anywhere has no picture to be matched against, and the page hides
  // the model's half of the button rather than asking over pictures of something else.
  showing: ReadonlySet<string> = NOTHING,
): ProductShot[] {
  const isComponent = groupOfCategory(category, useCategories()) === 'Components';
  const listing = useListingImages(isComponent ? docId : null);
  const { gallery, galleryViews } = useListing(docId ?? '');
  return useMemo(() => {
    const viewOf = new Map(gallery.map((url, i) => [url, galleryViews[i] || OTHER_DIRECTION]));
    // The gallery URL travels with the id, and it is not decoration: it is the key a part's
    // CROPS are stored under, so it is the only way to ask "which masks belong on this
    // photograph". See maskedShot.
    return listing
      .map(({ id, url }) => ({ id, url, view: viewOf.get(url) ?? OTHER_DIRECTION }))
      .filter((s) => showing.has(s.url) && s.view !== OTHER_DIRECTION)
      .sort((a, b) => directionRank(a.view) - directionRank(b.view))
      .slice(0, MAX_PRODUCT_SHOTS);
  }, [listing, gallery, galleryViews, showing]);
}

// What goes with the question when nobody has chosen: the first drawings, and — for a
// component — the first photographs of the thing it belongs to. The same two the server
// picked on its own before any of this was pickable, plus the context that was never
// offered at all.
export const defaultPicks = (groups: AskGroup[]): PictureRef[] =>
  groups.flatMap((g) => g.refs.slice(0, g.kind === 'schematic' ? 2 : g.max));
